import { useState, useMemo } from "react"
import type {
  ICLockProduct,
  ILlPhyProduct,
  IProduct,
  ProductType,
} from "@repo/loomlock/model-types"
import { useProductsWithListings } from "./useProductsWithListings"
import type { WithListing } from "../types"


type FilterType = "all" | ProductType | "listed"
type SortType = "newest" | "name" | "listing-date" | "price-low" | "price-high"

export interface NFTGridItem {
  nft: WithListing<IProduct>
  type: ProductType
}

export function useNFTsGrid() {
  const [filterType, setFilterType] = useState<FilterType>("all")
  const [sortType, setSortType] = useState<SortType>("newest")
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(12)

  const { products, isLoading } = useProductsWithListings()

  const processedNFTs = useMemo(() => {
    let allNFTs: NFTGridItem[] = []

    // TODO: we'll want this to be generic on ProductTypes
    products?.forEach((product) => {
      const isClock = product.productType === "cLock"
      const isPhygital = product.productType === "llPhygital"
      const hasActiveListings = product.listings.length > 0

      if (filterType === "listed" && !hasActiveListings) {
        return
      }

      // TODO: make this agnostic on ProductType
      if (
        isClock &&
        (filterType === "all" ||
          filterType === "cLock" ||
          filterType === "listed")
      ) {
        allNFTs.push({
          nft: product as WithListing<ICLockProduct>,
          type: "cLock",
        })
      } else if (
        isPhygital &&
        (filterType === "all" ||
          filterType === "llPhygital" ||
          filterType === "listed")
      ) {
        allNFTs.push({
          nft: product as WithListing<ILlPhyProduct>,
          type: "llPhygital",
        })
      }
    })

    if (searchTerm) {
      allNFTs = allNFTs.filter(({ nft, type }) => {
        const name =
          type === "cLock"
            ? (nft as ICLockProduct).metadata.name || `cLock #${nft.tokenId}`
            : (nft as ILlPhyProduct).metadata.name || `Phygital #${nft.tokenId}`
        const description =
          type === "cLock"
            ? (nft as ICLockProduct).metadata.description || ""
            : (nft as ILlPhyProduct).metadata.description || ""

        return (
          name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          nft.tokenId.toString().includes(searchTerm)
        )
      })
    }

    allNFTs.sort((a, b) => {
      const aProduct = products?.find((p) => p.tokenId === a.nft.tokenId)
      const bProduct = products?.find((p) => p.tokenId === b.nft.tokenId)
      const aListings = aProduct?.listings || []
      const bListings = bProduct?.listings || []

      switch (sortType) {
        case "newest":
          return Number(b.nft.tokenId) - Number(a.nft.tokenId)

        case "listing-date": {
          const aLatestListing = aListings[0]
          const bLatestListing = bListings[0]

          if (aLatestListing && bLatestListing) {
            return (
              Number(bLatestListing.startTimestamp) -
              Number(aLatestListing.startTimestamp)
            )
          }
          if (aLatestListing) {
            return -1
          }
          if (bLatestListing) {
            return 1
          }
          return Number(b.nft.tokenId) - Number(a.nft.tokenId)
        }

        case "price-low": {
          const aLowestPrice =
            aListings.length > 0
              ? Math.min(...aListings.map((l) => Number(l.pricePerToken)))
              : Infinity
          const bLowestPrice =
            bListings.length > 0
              ? Math.min(...bListings.map((l) => Number(l.pricePerToken)))
              : Infinity

          if (aLowestPrice === Infinity && bLowestPrice === Infinity) {
            return 0
          }
          if (aLowestPrice === Infinity) {
            return 1
          }
          if (bLowestPrice === Infinity) {
            return -1
          }
          return aLowestPrice - bLowestPrice
        }

        case "price-high": {
          const aHighestPrice =
            aListings.length > 0
              ? Math.max(...aListings.map((l) => Number(l.pricePerToken)))
              : -Infinity
          const bHighestPrice =
            bListings.length > 0
              ? Math.max(...bListings.map((l) => Number(l.pricePerToken)))
              : -Infinity

          if (aHighestPrice === -Infinity && bHighestPrice === -Infinity) {
            return 0
          }
          if (aHighestPrice === -Infinity) {
            return 1
          }
          if (bHighestPrice === -Infinity) {
            return -1
          }
          return bHighestPrice - aHighestPrice
        }

        case "name": {
          const aName = a.nft.metadata.name
          const bName = b.nft.metadata.name
          return aName.localeCompare(bName)
        }

        default:
          return 0
      }
    })

    return allNFTs
  }, [products, filterType, sortType, searchTerm])

  const paginatedNFTs = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return processedNFTs.slice(startIndex, endIndex)
  }, [processedNFTs, currentPage, pageSize])

  return {
    filterType,
    setFilterType,
    sortType,
    setSortType,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    pageSize,
    paginatedNFTs,
    totalNFTs: processedNFTs.length,
    isLoading,
    hasNFTs: Boolean(products?.length),
  }
}
